import React from 'react'
import { useTranslation } from 'react-i18next'
import { Bell, Check, ChevronRight } from 'lucide-react'
import { RecurringIcon } from '@/src/renderer/components/icons'
import { useMenuKit } from './menuKit'
import { ReminderMenuContent } from './ReminderMenuContent'

/*
  Die beiden Zeilenarten des **Aufgaben-Menüs** — geteilt von `TaskMenu` und `SubtaskMenu`
  (#205), damit beide Menüs gleich aussehen und gleich reagieren.
*/

/**
 * Eine Zeile, die ein Unter-Menü aufklappt: Zeichen, Beschriftung, dann rechts
 * Haken, Kürzel und Pfeil.
 *
 * Der **Haken** sagt „das ist gesetzt" — nicht der Wert. Das Menü ist zum
 * Anfassen da, nicht zum Nachlesen; die Werte stehen ohnehin als Marken an der
 * Zeile, und verschieden lange Werte machten die rechte Kante unruhig und
 * verdrängten das Kürzel ausgerechnet dort, wo man es am ehesten braucht
 * (CONTEXT.md, Aufgaben-Menü).
 */
export const MenuSub: React.FC<{
  icon: React.ReactNode
  label: string
  /** Fehlt, wo es keine Taste gibt (Umhängen an einer Unteraufgabe). */
  shortcut?: string
  marked: boolean
  open: boolean
  onOpenChange: (open: boolean) => void
  disabled?: boolean
  children: React.ReactNode
}> = ({ icon, label, shortcut, marked, open, onOpenChange, disabled, children }) => {
  const kit = useMenuKit()
  return (
    <kit.Sub open={open} onOpenChange={onOpenChange}>
      <kit.SubTrigger disabled={disabled}>
        <span className="context-menu-content__item-icon">{icon}</span>
        {label}
        <kit.ItemTrailing>
          {marked && <Check className="context-menu-content__item-check" size={14} strokeWidth={2} />}
          {shortcut && <kit.Shortcut>{shortcut}</kit.Shortcut>}
          <ChevronRight className="context-menu-content__sub-chevron" size={14} strokeWidth={2} />
        </kit.ItemTrailing>
      </kit.SubTrigger>
      <kit.SubContent>{children}</kit.SubContent>
    </kit.Sub>
  )
}

/** Eine Zeile, die sofort etwas tut. */
export const MenuAction: React.FC<{
  icon: React.ReactNode
  label: string
  shortcut?: string
  danger?: boolean
  /** Grau, aber da: Die Zeile sagt, was es hier gäbe — nur nicht für diese Aufgabe. */
  disabled?: boolean
  onSelect: () => void
}> = ({ icon, label, shortcut, danger, disabled, onSelect }) => {
  const kit = useMenuKit()
  return (
    <kit.Item
      className={danger ? 'context-menu-content__item--danger' : undefined}
      disabled={disabled}
      onSelect={onSelect}
    >
      <span className="context-menu-content__item-icon">{icon}</span>
      {label}
      {shortcut && (
        <kit.ItemTrailing>
          <kit.Shortcut>{shortcut}</kit.Shortcut>
        </kit.ItemTrailing>
      )}
    </kit.Item>
  )
}

/** Unter einer wiederkehrenden Aufgabe gilt deren Rhythmus (#205): statt „Wiederkehrend …" diese graue Zeile. */
export const RecurrenceLockedRow: React.FC = () => {
  const { t } = useTranslation()
  return (
    <MenuAction
      icon={<RecurringIcon size={16} />}
      label={t('taskItem.menu.recurrenceWithParent')}
      disabled
      onSelect={() => {}}
    />
  )
}

/**
 * **„Erinnerung“** (Plan task-reminders) — am Ende der Zeit-Gruppe, in beiden Menüs. Nur für EINE
 * Aufgabe: Erinnerungen hängen an ihr, nicht an einer Auswahl; bei mehreren sagt die graue Zeile es.
 * Der Inhalt fragt die Erinnerungen erst ab, wenn das Unter-Menü aufgeht.
 */
export const ReminderMenuSub: React.FC<{
  /** `undefined`: mehrere gemeint. */
  taskId: string | undefined
  marked: boolean
  open: boolean
  onOpenChange: (open: boolean) => void
  onOpenDetails?: () => void
}> = ({ taskId, marked, open, onOpenChange, onOpenDetails }) => {
  const { t } = useTranslation()
  if (!taskId) {
    return <MenuAction icon={<Bell size={16} />} label={t('reminders.menu.multi')} disabled onSelect={() => {}} />
  }
  return (
    <MenuSub icon={<Bell size={16} />} label={t('taskItem.menu.reminders')} marked={marked} open={open} onOpenChange={onOpenChange}>
      <ReminderMenuContent taskId={taskId} onOpenDetails={onOpenDetails} />
    </MenuSub>
  )
}
